// Zone detection — resolves visitor shipping zone for AE Foodstore.
// Priority chain (BUG-P fix 2026-05-17 — user-entered ZIP now beats stale cache):
//   0. localStorage.ae_zip_code (set by ae-delivery-widget) → derive state via
//      zipToState() prefix table — authoritative when user has explicitly set ZIP
//   1. cart.shipping_address.province_code (real shipping address from checkout)
//   2. localStorage cache (30d TTL) — invalidated on read if postal != ae_zip_code
//   3. Shopify.country check (hide bar if non-US)
//   4. ipapi.co fetch (state + zip from IP)
//   5. Fallback: us_far ($79 conservative)
// Dispatches `ae-zone-ready` event with detail { zone, threshold, label, source }.
// Listens for `aef:zone-changed` to re-resolve in-page (no reload) when widget
// captures a new ZIP. Bot detection skips ipapi. Failure: 3 fails → 24h cooldown.
(function () {
  'use strict';

  const ZONES = {
    dfw:         { threshold: 5900, label: 'FREE local delivery', sub: 'Same-day delivery' },
    tx_local:    { threshold: 5900, label: 'FREE shipping',       sub: '' },
    us_standard: { threshold: 6900, label: 'FREE shipping',       sub: '' },
    us_far:      { threshold: 7900, label: 'FREE shipping',       sub: '' },
    remote:      { threshold: null, label: 'USPS calculated rate', sub: '' },
  };

  /* TX Local: TX, OK ($59) */
  /* US Standard: 25 states ($69) */
  /* US Far Extended: 22 states ($79) */
  /* Remote: AK, HI, PR (USPS) */
  const STATE_ZONE = {
    'TX': 'tx_local', 'OK': 'tx_local',
    'AR': 'us_standard', 'LA': 'us_standard', 'MS': 'us_standard',
    'AL': 'us_standard', 'TN': 'us_standard', 'KY': 'us_standard',
    'MO': 'us_standard', 'KS': 'us_standard', 'IA': 'us_standard',
    'IL': 'us_standard', 'IN': 'us_standard', 'OH': 'us_standard',
    'GA': 'us_standard', 'FL': 'us_standard', 'SC': 'us_standard',
    'NC': 'us_standard', 'VA': 'us_standard', 'WV': 'us_standard',
    'MD': 'us_standard', 'DE': 'us_standard', 'DC': 'us_standard',
    'PA': 'us_standard', 'NM': 'us_standard', 'NE': 'us_standard',
    'NJ': 'us_standard',
    'CA': 'us_far', 'OR': 'us_far', 'WA': 'us_far',
    'NV': 'us_far', 'ID': 'us_far', 'UT': 'us_far',
    'AZ': 'us_far', 'CO': 'us_far', 'WY': 'us_far',
    'MT': 'us_far', 'ND': 'us_far', 'SD': 'us_far',
    'MN': 'us_far', 'WI': 'us_far', 'MI': 'us_far',
    'NY': 'us_far', 'CT': 'us_far', 'RI': 'us_far',
    'MA': 'us_far', 'VT': 'us_far', 'NH': 'us_far',
    'ME': 'us_far',
    'AK': 'remote', 'HI': 'remote', 'PR': 'remote'
  };

  const FALLBACK_ZONE = 'us_far';
  const CACHE_KEY = 'ae_zone_v1';
  const ZIP_KEY = 'ae_zip_code';
  const CACHE_TTL = 30 * 24 * 60 * 60 * 1000;
  const FAIL_KEY = 'ae_zone_fail_v1';
  const FAIL_LIMIT = 3;
  const FAIL_COOLDOWN = 24 * 60 * 60 * 1000;

  /* BUG-P fix: US ZIP code first-3-digit → state mapping. The ae-delivery-widget
     captures a ZIP via UI but doesn't know the state directly (only tier 1–5 by
     distance). We need the state to look up the correct zone via STATE_ZONE.
     Sources: USPS ZIP code zone allocations + Wikipedia US ZIP prefix tables.
     Ranges are inclusive on both ends. First-match wins; ordered by prefix. */
  const ZIP_PREFIX_RANGES = [
    [5,   8,   'NY'], [9,   9,   'PR'], [10,  27,  'MA'], [28,  29,  'RI'],
    [30,  38,  'NH'], [39,  49,  'ME'], [50,  59,  'VT'], [60,  69,  'CT'],
    [70,  89,  'NJ'], [100, 149, 'NY'], [150, 196, 'PA'], [197, 199, 'DE'],
    [200, 205, 'DC'], [206, 219, 'MD'], [220, 246, 'VA'], [247, 268, 'WV'],
    [270, 289, 'NC'], [290, 299, 'SC'], [300, 319, 'GA'], [320, 349, 'FL'],
    [350, 369, 'AL'], [370, 385, 'TN'], [386, 397, 'MS'], [398, 399, 'GA'],
    [400, 427, 'KY'], [430, 458, 'OH'], [460, 479, 'IN'], [480, 499, 'MI'],
    [500, 528, 'IA'], [530, 549, 'WI'], [550, 567, 'MN'], [570, 577, 'SD'],
    [580, 588, 'ND'], [590, 599, 'MT'], [600, 629, 'IL'], [630, 658, 'MO'],
    [660, 679, 'KS'], [680, 693, 'NE'], [700, 714, 'LA'], [716, 729, 'AR'],
    [730, 749, 'OK'], [750, 799, 'TX'], [800, 816, 'CO'], [820, 831, 'WY'],
    [832, 838, 'ID'], [840, 847, 'UT'], [850, 865, 'AZ'], [870, 884, 'NM'],
    [889, 898, 'NV'], [900, 961, 'CA'], [967, 968, 'HI'], [970, 979, 'OR'],
    [980, 994, 'WA'], [995, 999, 'AK']
  ];

  function zipToState(zip) {
    if (!zip) return null;
    const z = String(zip).trim();
    if (!/^\d{5}$/.test(z)) return null;
    const p3 = parseInt(z.substring(0, 3), 10);
    for (let i = 0; i < ZIP_PREFIX_RANGES.length; i++) {
      const r = ZIP_PREFIX_RANGES[i];
      if (p3 >= r[0] && p3 <= r[1]) return r[2];
    }
    return null;
  }

  function readSavedZip() {
    try {
      const z = localStorage.getItem(ZIP_KEY);
      return z && /^\d{5}$/.test(z) ? z : null;
    } catch (e) { return null; }
  }

  function isBot() {
    if (navigator.webdriver) return true;
    return /bot|crawl|spider|headless|googlebot|bingbot|slurp|duckduckbot|baiduspider|yandex/i.test(navigator.userAgent || '');
  }

  function isDFW(state, postal) {
    if (state !== 'TX' || !postal) return false;
    const z = parseInt(String(postal).slice(0, 5), 10);
    return z >= 75001 && z <= 76299;
  }

  function resolveZoneKey(state, postal) {
    if (isDFW(state, postal)) return 'dfw';
    return STATE_ZONE[state] || FALLBACK_ZONE;
  }

  function dispatch(zoneKey, source, detail) {
    const z = ZONES[zoneKey] || ZONES[FALLBACK_ZONE];
    const payload = Object.assign({}, z, { zone: zoneKey, source: source }, detail || {});
    window.AE_ZONE = payload;
    document.dispatchEvent(new CustomEvent('ae-zone-ready', { detail: payload }));
  }

  function loadCache() {
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      const d = JSON.parse(raw);
      if (Date.now() - d.t > CACHE_TTL) return null;
      /* BUG-P safety net: if the user changed ZIP via the widget after this
         cache was saved, the postal field is stale. Drop the entry so the
         resolve chain falls through to a fresh source. */
      const currentZip = readSavedZip();
      if (currentZip && d.postal && String(d.postal) !== String(currentZip)) {
        try { localStorage.removeItem(CACHE_KEY); } catch (e) {}
        return null;
      }
      return d;
    } catch (e) { return null; }
  }
  function saveCache(state, postal) {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({ t: Date.now(), state: state, postal: postal || '' }));
    } catch (e) {}
  }
  function isFailCooled() {
    try {
      const raw = localStorage.getItem(FAIL_KEY);
      if (!raw) return false;
      const d = JSON.parse(raw);
      if (d.count >= FAIL_LIMIT && Date.now() - d.t < FAIL_COOLDOWN) return true;
      return false;
    } catch (e) { return false; }
  }
  function recordFail() {
    try {
      const raw = localStorage.getItem(FAIL_KEY);
      const d = raw ? JSON.parse(raw) : { count: 0, t: Date.now() };
      d.count = (d.count || 0) + 1;
      if (d.count === 1) d.t = Date.now();
      localStorage.setItem(FAIL_KEY, JSON.stringify(d));
    } catch (e) {}
  }
  function clearFail() {
    try { localStorage.removeItem(FAIL_KEY); } catch (e) {}
  }

  async function detectFromCart() {
    try {
      const r = await fetch('/cart.js', { credentials: 'same-origin' });
      const cart = await r.json();
      const addr = cart.shipping_address;
      if (addr && addr.province_code) {
        return { state: addr.province_code, postal: addr.zip || '' };
      }
    } catch (e) {}
    return null;
  }

  async function detectFromIpapi() {
    if (isFailCooled()) return null;
    try {
      const r = await fetch('https://ipapi.co/json/', { mode: 'cors' });
      if (!r.ok) throw new Error('ipapi http ' + r.status);
      const d = await r.json();
      if (d.country !== 'US' && d.country_code !== 'US') return { state: null, country: d.country };
      const state = d.region_code || '';
      const postal = d.postal || '';
      if (state) {
        clearFail();
        return { state: state, postal: postal };
      }
    } catch (e) {
      recordFail();
    }
    return null;
  }

  async function resolve() {
    if (isBot()) {
      dispatch(FALLBACK_ZONE, 'bot');
      return;
    }

    /* 0. User-entered ZIP (BUG-P fix). If the visitor used the delivery
       widget to pick a ZIP, that's the most explicit signal of intent —
       trust it over cached IP guesses. zipToState() handles the 50-state
       prefix lookup; if the ZIP falls outside any known US range, fall
       through to the cart/cache/IP chain. */
    const userZip = readSavedZip();
    if (userZip) {
      const userState = zipToState(userZip);
      if (userState) {
        saveCache(userState, userZip);
        dispatch(resolveZoneKey(userState, userZip), 'user-zip', { state: userState, postal: userZip });
        return;
      }
    }

    /* 1. Cart shipping address */
    const cart = await detectFromCart();
    if (cart && cart.state) {
      saveCache(cart.state, cart.postal);
      dispatch(resolveZoneKey(cart.state, cart.postal), 'cart', { state: cart.state, postal: cart.postal });
      return;
    }

    /* 2. localStorage cache (loadCache also invalidates if postal != ae_zip_code) */
    const cached = loadCache();
    if (cached && cached.state) {
      dispatch(resolveZoneKey(cached.state, cached.postal), 'cache', { state: cached.state, postal: cached.postal });
      return;
    }

    /* 3. Shopify.country */
    if (window.Shopify && Shopify.country && Shopify.country !== 'US') {
      dispatch(FALLBACK_ZONE, 'non-us', { state: null });
      return;
    }

    /* 4. ipapi.co */
    const ip = await detectFromIpapi();
    if (ip && ip.state) {
      saveCache(ip.state, ip.postal);
      dispatch(resolveZoneKey(ip.state, ip.postal), 'ipapi', { state: ip.state, postal: ip.postal });
      return;
    }

    /* 5. Fallback */
    dispatch(FALLBACK_ZONE, 'fallback');
  }

  /* BUG-P fix: when ae-delivery-widget captures a new ZIP it dispatches
     `aef:zone-changed` after invalidating the stale `ae_zone_v1` cache.
     Re-running resolve() picks up the new ZIP via the priority-0 step
     and re-fires `ae-zone-ready` — free-shipping-bar, mini-cart popup,
     and any other listener re-render with the correct threshold. */
  window.addEventListener('aef:zone-changed', function () {
    try { resolve(); } catch (e) {}
  });

  /* Expose a manual re-resolve hook so other code paths can request a
     fresh zone after they've updated `ae_zip_code` in localStorage. */
  window.aeZoneResolve = function () { try { resolve(); } catch (e) {} };

  resolve();
})();
